import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { siteColour } from '../../_services/sites.service';
import { captureBrandLanding, brandLinkQuery } from '../../_utils/trackedSource';

const addressFor = (site) => [site.formattedAddress, site.neighborhood, site.postalCode]
    .filter(Boolean).filter((value, index, all) => all.indexOf(value) === index).join(' · ');

/** Public landing page for a multi-location brand. */
export default function PublicBrandPage({ data }) {
    const brand = (data && data.brand) || {};
    const sites = (data && data.sites) || [];
    // A tracked link (?src=) lands here once; the location they pick gets the
    // same src marked via=brand, so it counts as their choice, not a new visit.
    useEffect(() => { captureBrandLanding(brand.slug); }, [brand.slug]);
    const passthrough = typeof window !== 'undefined' ? brandLinkQuery(window.location.search) : '';

    return (
        <main style={{ minHeight: '100vh', background: '#F7F7F9', color: '#1A1A1E' }}>
            <Helmet>
                <title>{brand.name ? `${brand.name} locations | Beu` : 'Choose a location | Beu'}</title>
                <meta name="description" content={`Choose a ${brand.name || 'Beu'} location and book online.`} />
            </Helmet>
            <section style={{ maxWidth: 920, margin: '0 auto', padding: 'clamp(28px, 7vw, 72px) 20px 56px' }}>
                <div style={{ marginBottom: 34 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.12em', color: '#007EA0', marginBottom: 12 }}>BEU</div>
                    <h1 style={{ margin: 0, fontSize: 'clamp(34px, 7vw, 58px)', lineHeight: 1.02, letterSpacing: '-0.04em' }}>{brand.name}</h1>
                    <p style={{ margin: '14px 0 0', color: '#68686F', fontSize: 17 }}>Choose the location that suits you.</p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
                    {sites.map((site) => {
                        const href = `${site.bookingUrl || `/book/${site.slug}`}${passthrough}`;
                        return (
                            <a key={site._id} href={href} style={{ display: 'block', textDecoration: 'none', color: 'inherit', background: '#fff', border: '1px solid #E7E7EB', borderRadius: 18, padding: 22, boxShadow: '0 8px 28px rgba(26,26,30,0.05)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 14 }}>
                                    <span aria-hidden style={{ width: 12, height: 12, borderRadius: '50%', background: siteColour(site._id), flex: '0 0 auto' }} />
                                    <h2 style={{ margin: 0, fontSize: 20, letterSpacing: '-0.02em' }}>{site.salonName}</h2>
                                </div>
                                <p style={{ minHeight: 42, margin: 0, color: '#68686F', lineHeight: 1.5, fontSize: 14 }}>{addressFor(site) || 'Book online at this location'}</p>
                                {site.googleRating ? (
                                    <p style={{ margin: '14px 0 0', fontSize: 13, color: '#4B4B52' }}>★ {Number(site.googleRating).toFixed(1)}{site.googleRatingsTotal ? ` · ${site.googleRatingsTotal} reviews` : ''}</p>
                                ) : null}
                                <div style={{ marginTop: 22, display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#007EA0', fontSize: 14, fontWeight: 750 }}>
                                    <span>View and book</span><span aria-hidden>→</span>
                                </div>
                            </a>
                        );
                    })}
                </div>

                {!sites.length && <p style={{ color: '#68686F' }}>No locations are available for online booking yet.</p>}
            </section>
        </main>
    );
}
